import { Link, Outlet, useLocation, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle } from "@phosphor-icons/react";

import { SiteFooter } from "../../../components/SiteFooter";
import { useAuth } from "../../../providers/AuthProvider";
import { AUTH_REDIRECT_KEY } from "../../../providers/AuthProvider";
import { storage } from "../api/storage";
import { getNextPathFromSearch, sanitizeRedirectPath } from "../../../utils/navigation";
import { defaultIconProps } from "../../shared/ui/icons";
import { useScrollToAnchor } from "../../../hooks/useScrollToAnchor";

const layoutWrapperClasses =
  "min-h-screen bg-slate-100 px-safe px-4 pb-safe pt-safe sm:px-6 lg:px-12 lg:py-10";
const layoutInnerClasses =
  "mx-auto flex min-h-[70vh] max-w-6xl flex-col gap-8 lg:py-6";
const layoutGridClasses =
  "grid flex-1 overflow-hidden rounded-none bg-white shadow-none sm:rounded-[32px] sm:shadow-2xl lg:grid-cols-[1.15fr_0.85fr]";
const heroSectionClasses =
  "flex flex-col gap-10 bg-gradient-to-b from-slate-900 via-slate-900 to-slate-800 px-8 py-10 text-white";
const heroHeaderClasses = "flex items-center gap-4";
const heroLogoClasses =
  "flex h-14 w-14 items-center justify-center rounded-2xl border border-white/10 bg-white/10 shadow-inner";
const heroBrandTextClasses = "flex flex-col gap-0.5";
const heroBrandTitleClasses = "text-2xl font-bold";
const heroBrandTaglineClasses = "text-sm font-semibold text-white/75";
const heroSubtitleClasses = "text-3xl font-semibold leading-tight";
const heroDescriptionClasses = "text-base leading-relaxed text-white/80";
const formSectionClasses =
  "flex items-center justify-center px-6 py-10 sm:px-10";
const formStackClasses = "flex w-full max-w-md flex-col gap-6";
const footerPromptClasses =
  "text-center text-sm text-slate-500";
const footerLinkClasses =
  "font-semibold text-slate-900 hover:text-slate-700";
const footerInlineClasses =
  "border-t border-slate-200 pt-4 text-sm text-slate-500 sm:pt-5 dark:border-slate-800";
const valueListClasses = "mt-4 space-y-3 text-white/85";
const valueItemTitleClasses = "text-sm font-semibold text-white";
const valueItemBodyClasses = "text-sm leading-relaxed text-white/80";

export function AuthLayout() {
  const location = useLocation();
  const path = location.pathname;
  const isLogin = path === "/login";
  const { t } = useTranslation();
  const valueKeys = ["geodetic", "marketplace", "buyers", "collaboration"] as const;
  useScrollToAnchor();
  const footerConfig = (() => {
    switch (path) {
      case "/login":
        return { key: "login", alternateTo: "/register" as const };
      case "/register":
        return { key: "register", alternateTo: "/login" as const };
      case "/forgot-password":
        return { key: "forgot_password", alternateTo: "/login" as const };
      case "/reset-password":
        return { key: "reset_password", alternateTo: "/login" as const };
      default:
        return null;
    }
  })();
  const footer = footerConfig
    ? {
        prompt: t(`auth.layout.footer.${footerConfig.key}.prompt`),
        alternateLabel: t(`auth.layout.footer.${footerConfig.key}.action`),
        alternateTo: footerConfig.alternateTo
      }
    : null;
  const heroFooterText = path === "/login" ? null : t("auth.login.welcome_back");
  const { user } = useAuth();
  const nextParam = getNextPathFromSearch(location.search);
  const storedRedirect = sanitizeRedirectPath(storage.get<string>(AUTH_REDIRECT_KEY));
  const redirectTarget = nextParam ?? storedRedirect ?? "/dashboard";

  if (user) {
    storage.remove(AUTH_REDIRECT_KEY);
    return <Navigate to={redirectTarget} replace />;
  }

  return (
    <div className={layoutWrapperClasses}>
      <div className={layoutInnerClasses}>
        <div className={layoutGridClasses}>
          <section className={heroSectionClasses} aria-label="LotLocator overview">
            <div className={heroHeaderClasses}>
              <span className={heroLogoClasses}>
                <img
                  src="/lotlocator-icon.png"
                  alt="LotLocator logo"
                  width={48}
                  height={48}
                  loading="lazy"
                  decoding="async"
                />
              </span>
              <div className={heroBrandTextClasses}>
                <p className={heroBrandTitleClasses}>LotLocator.ph</p>
                <p className={heroBrandTaglineClasses}>{t("dashboard.tagline")}</p>
              </div>
            </div>
            <div className="space-y-5">
              <h1 className={heroSubtitleClasses}>{t("auth.layout.hero.subtitle")}</h1>
              <p className={heroDescriptionClasses}>{t("auth.layout.hero.description")}</p>
              <a
                href="#auth-form"
                className="pm-btn pm-btn-tonal w-full text-center md:hidden"
              >
                {isLogin ? t("auth.login.scroll_to_form") : t("auth.layout.hero.scroll_to_form")}
              </a>
              <div className={valueListClasses} aria-label={t("auth.login.value_title")}>
                {valueKeys.map((key) => (
                  <div key={key} className="flex items-start gap-3">
                    <CheckCircle {...defaultIconProps} className="mt-1 text-emerald-300" aria-hidden />
                    <div className="space-y-1">
                      <p className={valueItemTitleClasses}>{t(`auth.login.value_points.${key}.title`)}</p>
                      <p className={valueItemBodyClasses}>{t(`auth.login.value_points.${key}.body`)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {heroFooterText ? (
              <div className="mt-auto">
                <p className="text-sm text-white/70">
                  {heroFooterText}
                </p>
              </div>
            ) : null}
          </section>

          <section className={formSectionClasses} aria-label="Authentication form">
            <div className={formStackClasses} id="auth-form">
              <Outlet />

              {footer ? (
                <p className={footerPromptClasses}>
                  {footer.prompt}{" "}
                  <Link to={footer.alternateTo} className={footerLinkClasses}>
                    {footer.alternateLabel}
                  </Link>
                </p>
              ) : null}

              <SiteFooter
                variant="inline"
                className={footerInlineClasses}
                showSignout={false}
              />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
